"use client";
import React, { useState, useRef, useEffect } from "react";
import { IoClose } from "react-icons/io5";
import { FiCheckCircle } from "react-icons/fi";
import { FiXCircle } from "react-icons/fi";
import {
  testConnection,
  startImport,
  pollImportStatus,
  loadPreviewData,
  executeMigration,
  MetabaseConnectionData,
  MetabaseImportJobData,
  MetabaseMigrationData,
} from "~/lib/network/metabase-connection";
import CredentialsForm from "./credentials-form";
import DiscoverContent from "./discover-content";
import Migration from "./migration";

type MetabaseConnectorProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const MetabaseConnector: React.FC<MetabaseConnectorProps> = ({
  open,
  onOpenChange,
}) => {
  const modalRef = useRef<HTMLDialogElement>(null);
  const [formData, setFormData] = useState<MetabaseConnectionData>({
    metabaseUrl: "",
    apiKey: "",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [isSuccessful, setIsSuccessful] = useState<boolean>(false);
  const [isCalloutExiting, setIsCalloutExiting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [step, setStep] = useState<number>(1);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [jobId, setJobId] = useState<string | null>(null);

  const handleInputChange = (
    field: keyof MetabaseConnectionData,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    if (open && modalRef.current) {
      modalRef.current?.showModal();
    } else if (!open && modalRef.current) {
      modalRef.current?.close();
    }
  }, [open]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setFormData({
        metabaseUrl: "",
        apiKey: "",
      });
      setIsLoading(false);
      setIsConnected(null);
      setIsSuccessful(false);
      setIsCalloutExiting(false);
      setStep(1);
      setPreviewData([]);
      setJobId(null);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Clear form data after successful connection

      await testConnection(formData);

      setIsConnected(true);
      setIsSuccessful(true);
      setIsCalloutExiting(false);
      setTimeout(() => {
        setIsCalloutExiting(true);
        setTimeout(() => {
          setIsConnected(null);
          setIsCalloutExiting(false);
        }, 300);
      }, 2700);
    } catch (error: any) {
      setIsLoading(false);
      setIsConnected(false);
      setIsSuccessful(false);
      setIsCalloutExiting(false);
      setErrorMessage(error?.response?.data?.detail);

      setTimeout(() => {
        setIsCalloutExiting(true);
        setTimeout(() => {
          setIsConnected(null);
          setIsCalloutExiting(false);
        }, 300);
      }, 4000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const isFormValid = () => {
    return formData?.metabaseUrl.trim() && formData?.apiKey.trim();
  };

  const handleNext = () => {
    setStep((prev) => prev + 1);
    setIsSuccessful(false);
  };

  return (
    <dialog ref={modalRef} className="modal">
      <div className="modal-box max-w-4xl max-h-[calc(100vh-2rem)] flex flex-col">
        <div className="flex justify-between items-center mb-4 flex-shrink-0">
          <h3 className="font-bold text-lg">Connect to Metabase</h3>
          <button
            className="btn btn-ghost btn-circle btn-sm"
            onClick={handleCancel}
          >
            <IoClose size={20} />
          </button>
        </div>
        <div className="w-full flex-shrink-0 mt-2">
          <ul className="steps w-full">
            <li className="step step-primary">Connection</li>
            <li className={`step ${step > 1 ? "step-primary" : ""}`}>
              Discover Content
            </li>
            <li className={`step ${step > 2 ? "step-primary" : ""}`}>
              Execute Migration
            </li>
          </ul>
        </div>
        <div className="bg-base-200 mt-8 rounded-lg p-6 overflow-y-auto max-h-lg">
          <div className="w-full">
            {step === 1 && (
              <CredentialsForm
                formData={formData}
                onInputChange={handleInputChange}
                onSubmit={handleSubmit}
                isLoading={isLoading}
                isFormValid={isFormValid}
                isSuccessful={isSuccessful}
                handleNext={handleNext}
              />
            )}
            {step === 2 && (
              <DiscoverContent
                connectionData={formData}
                isSuccessful={isSuccessful}
                handleNext={handleNext}
                setIsSuccessful={setIsSuccessful}
                previewData={previewData}
                setPreviewData={setPreviewData}
                setJobId={setJobId}
                jobId={jobId}
              />
            )}
            {step === 3 && (
              <Migration
                previewData={previewData}
                handleCancel={handleCancel}
                jobId={jobId}
              />
            )}
          </div>
        </div>
      </div>
      {isConnected !== null && (
        <div className="toast toast-top toast-center z-50">
          <div
            className={`alert ${
              isConnected ? "alert-success" : "alert-error"
            } ${
              isCalloutExiting
                ? "opacity-0 -translate-y-full"
                : "opacity-100 translate-y-0"
            } transition-all duration-300 ease-in-out`}
          >
            {isConnected ? (
              <FiCheckCircle className="h-6 w-6" />
            ) : (
              <FiXCircle className="h-6 w-6" />
            )}
            <span>{isConnected ? "Connection successful" : errorMessage}</span>
          </div>
        </div>
      )}
    </dialog>
  );
};

export default MetabaseConnector;
